
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Loader2, RefreshCw, Calendar, Banknote, ShieldAlert, Info } from "lucide-react";

/** helpers */
function safe(v: any, fallback = "-") {
  if (v === null || v === undefined || v === "") return fallback;
  return String(v);
}
function nf(v: any) {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : String(v);
}

type PromiseRow = {
  id?: number | string;
  loan_id?: number | string;
  amount?: number | string;
  date?: string;
  status_text?: string;
};

type PaginationMeta = {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
};

export default function Promise2PayTab({ customerId }: { customerId: string }) {
  const perPage = 10;
  const location = useLocation();
  const nonce = (location.state as any)?.nonce;

  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const isRTL = dir === "rtl";

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState<PromiseRow[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);

  const totalAmount = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [rows]
  );

  function statusPill(status?: string) {
    const s = (status || "").toLowerCase();
    if (s.includes("paid")) return "bg-emerald-500/10 text-emerald-700 border-emerald-200";
    if (s.includes("not")) return "bg-amber-500/10 text-amber-700 border-amber-200";
    return "bg-primary/10 text-primary border-primary/20";
  }

  const inFlight = React.useRef(false);

async function fetchRows(p = 1) {
  if (!customerId) return;
  if (inFlight.current) return;

  inFlight.current = true;
  setLoading(true);
  setErr("");

  try {
    const res = await apiFetch<any>(`/customer-profile/${customerId}/promise-to-pay?page=${p}&per_page=${perPage}`);
    const payload = res?.data ?? res;

    const data: PromiseRow[] = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
      ? payload
      : [];

    const m: PaginationMeta | null = payload?.meta ?? null;

    setRows(data);
    setMeta(m);
    setPage(p);
  } catch (e: any) {
    setErr(e?.message || "Failed to load promise to pay");
    setRows([]);
    setMeta(null);
  } finally {
    setLoading(false);
    inFlight.current = false;
  }
}

  useEffect(() => {
    fetchRows(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, nonce]);

  if (loading && rows.length === 0) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-6 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading promise to pay...
        </CardContent>
      </Card>
    );
  }

  if (err) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-4 space-y-3">
          <div className="text-sm text-destructive">{err}</div>
          <Button variant="secondary" className="gap-2" onClick={() => fetchRows(1)}>
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-semibold inline-flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-primary" />
          Promise To Pay
        </div>

        <div className="flex items-center gap-2">
          <Badge className="bg-muted text-foreground border">
            Total: {nf(meta?.total ?? rows.length)}
          </Badge>
          <Button variant="secondary" onClick={() => fetchRows(1)} className="gap-2" disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* desktop table */}
      <div className="hidden lg:block overflow-x-auto rounded-xl border">
        <table className="min-w-[900px] w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="p-3">#</th>
              <th className="p-3">Loan ID</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Date</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>

          <tbody>
            {rows.length ? (
              <>
                {rows.map((r, idx) => (
                  <tr key={String(r.id ?? idx)} className="border-t">
                    <td className="p-3">{idx + 1}</td>
                    <td className="p-3">{safe(r.loan_id)}</td>
                    <td className="p-3 font-semibold">{nf(r.amount)}</td>
                    <td className="p-3">{safe(r.date)}</td>
                    <td className="p-3">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border",
                          statusPill(safe(r.status_text, "Not Paid"))
                        )}
                      >
                        <Info className="h-3.5 w-3.5" />
                        {safe(r.status_text, "Not Paid")}
                      </span>
                    </td>
                  </tr>
                ))}

                <tr className="border-t bg-muted/30">
                  <td className="p-3 font-semibold" colSpan={2}>
                    Total
                  </td>
                  <td className="p-3 font-semibold">{nf(totalAmount)}</td>
                  <td className="p-3" colSpan={2} />
                </tr>
              </>
            ) : (
              <tr>
                <td className="p-4 text-muted-foreground" colSpan={5}>
                  No promise-to-pay entries.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* mobile cards */}
      <div className="lg:hidden space-y-3">
        {rows.length ? (
          <>
            {rows.map((r, idx) => (
              <Card key={String(r.id ?? idx)} className="shadow-card rounded-2xl border bg-background">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground">Loan</div>
                      <div className="text-sm font-semibold truncate">{safe(r.loan_id)}</div>
                    </div>

                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border",
                        statusPill(safe(r.status_text, "Not Paid"))
                      )}
                    >
                      <Info className="h-3.5 w-3.5" />
                      {safe(r.status_text, "Not Paid")}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl border bg-muted/20 p-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <Banknote className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[11px] text-muted-foreground font-medium">Amount</div>
                          <div className="text-sm font-semibold truncate">{nf(r.amount)}</div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border bg-muted/20 p-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <Calendar className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[11px] text-muted-foreground font-medium">Date</div>
                          <div className="text-sm font-semibold truncate">{safe(r.date)}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground">#{idx + 1}</div>
                </CardContent>
              </Card>
            ))}

            <Card className="shadow-card">
              <CardContent className="p-3 flex items-center justify-between">
                <div className="text-sm font-semibold">Total</div>
                <div className="text-sm font-semibold">{nf(totalAmount)}</div>
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-sm text-muted-foreground">No promise-to-pay entries.</div>
        )}
      </div>

      {/* pagination */}
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground">
          {meta ? (
            <>
              Page {meta.current_page} of {meta.last_page} • Total {meta.total}
            </>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={loading || page <= 1} onClick={() => fetchRows(Math.max(1, page - 1))}>
            Prev
          </Button>
          <Button
            variant="outline"
            disabled={loading || !(meta?.has_more ?? false)}
            onClick={() => fetchRows(page + 1)}
            className="gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}